/**
 * useSite Hook
 *
 * Custom hook for accessing the site context.
 * Must be used within a SiteProvider component.
 *
 * Requirements: 2.1 - THE Site_API_Client SHALL be implemented as a separate module from other application code
 */

import { useContext } from 'react';
import { SiteContext, type SiteContextValue } from './SiteContext';

/**
 * Hook to access the site context value.
 * Provides access to site state and actions (loadSite, clearSite).
 *
 * @returns The site context value containing site state and actions
 * @throws Error if used outside of a SiteProvider
 *
 * @example
 * ```tsx
 * function SiteDisplay() {
 *   const { site, isLoading, error, loadSite, clearSite } = useSite();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *   if (!site) return <div>No site loaded</div>;
 *
 *   return <div>{site.name}</div>;
 * }
 * ```
 */
export function useSite(): SiteContextValue {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
}
